import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import AuthSystem from './shared/auth/auth.js';
import Navigation from './shared/routing/navigation.js';
import Particles from './components/Particles.js';
import Logo from './components/Logo.js';
import ModalPermissoes from './components/ModalPermissoes.js';
import ModalTarefas from './components/ModalTarefas.js';
import PermissionHelper from './utils/permissionHelper.js';
import { API_URLS, FRONTEND_URLS } from './config/api';

// Módulos com lazy loading
const ComprasModule = React.lazy(() => import('./modules/ComprasModule'));
const LicitacaoModule = React.lazy(() => import('./modules/LicitacaoModule'));
const FinanceiroModule = React.lazy(() => import('./modules/FinanceiroModule'));
const OperacionalModule = React.lazy(() => import('./modules/OperacionalModule'));
const TreinamentosModule = React.lazy(() => import('./modules/TreinamentosModule'));
const RecursosHumanosModule = React.lazy(() => import('./modules/RecursosHumanosModule'));
const BaseContratosAtivos = React.lazy(() => import('./modules/BaseContratosAtivos/BaseContratosAtivos'));
const RelatoriosPowerBi = React.lazy(() => import('./modules/RelatoriosPowerBi/App'));
const SeiInternoModule = React.lazy(() => import('./modules/SeiInternoModule'));
const SolicitarCompraApp = React.lazy(() => import('./modules/SolicitarCompra/App'));
const SolicitarErpApp = React.lazy(() => import('./modules/SolicitarErp/App'));
const SolicitarSetorSistemasApp = React.lazy(() => import('./modules/SolicitarSetorSistemas/App'));

// JavaScript antigo removido - funcionalidades implementadas em React

function App() {
  const [user, setUser] = useState(null);
  const [sidebarActive, setSidebarActive] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [permissoesModalOpen, setPermissoesModalOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [tarefasModalOpen, setTarefasModalOpen] = useState(false);
  const [tarefaEdicao, setTarefaEdicao] = useState(null);
  const [tarefas, setTarefas] = useState([]);
  const [badgeCompras, setBadgeCompras] = useState(0);
  const [showModalSistemas, setShowModalSistemas] = useState(false);

  // Função para verificar autenticação
  const checkAuthentication = () => {
    console.log('🔍 Verificando autenticação...');
    
    // Debug: Vamos ver o que tem no sessionStorage
    const sessionData = sessionStorage.getItem('erp_user_session');
    console.log('📊 Dados no sessionStorage:', sessionData);
    
    const isAuth = AuthSystem.isAuthenticated();
    console.log('🔐 Resultado isAuthenticated:', isAuth);
    
    if (!isAuth) {
      console.log('❌ Usuário não autenticado');
      return false;
    }

    // Carregar dados do usuário se autenticado
    console.log('✅ Usuário autenticado - carregando dados');
    const currentUser = AuthSystem.getCurrentUser();
    console.log('👤 Dados do usuário:', currentUser);
    setUser(currentUser);
    return true;
  };

  // Função para carregar tarefas
  const carregarTarefas = async () => {
    try {
      const response = await fetch(API_URLS.TAREFAS);
      
      // Verificar se a resposta é JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('⚠️ Backend de tarefas não está respondendo corretamente');
        console.warn('💡 Certifique-se de que o backend está rodando na porta 3020');
        setTarefas([]); // Define array vazio
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setTarefas(data.data);
        console.log('✅ Tarefas carregadas:', data.data.length);
      } else {
        console.warn('⚠️ Erro ao carregar tarefas:', data.message);
        setTarefas([]);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tarefas:', error);
      console.warn('💡 Backend de tarefas pode não estar rodando na porta 3020');
      setTarefas([]); // Define array vazio para evitar erros
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loginParam = urlParams.get('login');

    const bootstrapAuth = () => {
      console.log('====================================');
      console.log('🚀 DASHBOARD GERAL - BOOTSTRAP AUTH');
      console.log('====================================');
      console.log('🌐 URL atual:', window.location.href);
      console.log('🔗 loginParam existe?', !!loginParam);
      console.log('🔗 loginParam length:', loginParam?.length || 0);
      console.log('====================================');
      
      // 1) Se veio do LoginInicial com dados na URL, faz login e limpa a URL
      if (loginParam) {
        try {
          console.log('🔗 Dados de login encontrados na URL');
          console.log('🔗 loginParam (URL encoded):', loginParam);
          
          // Decodificar URL (agora suporta UTF-8)
          const loginParamDecoded = decodeURIComponent(loginParam);
          console.log('🔗 loginParam decodificado:', loginParamDecoded);
          
          // Parsear JSON direto (sem btoa/atob)
          let userData;
          try {
            userData = JSON.parse(loginParamDecoded);
            console.log('✅ JSON parseado com sucesso');
            console.log('📦 userData completo:', userData);
          } catch (jsonError) {
            console.error('❌ ERRO ao parsear JSON:', jsonError);
            console.error('❌ String que falhou:', loginParamDecoded);
            throw jsonError;
          }
          
          console.log('📦 userData.userId:', userData.userId);
          console.log('📦 userData.cpf:', userData.cpf);
          
          const loginSuccess = AuthSystem.login(userData);
          console.log('🎯 AuthSystem.login retornou:', loginSuccess);
          
          if (loginSuccess) {
            console.log('✅ Login automático realizado via URL');
            console.log('👤 Usuário configurado:', userData.nome);
            setUser(userData);
            window.history.replaceState({}, document.title, window.location.pathname);
            console.log('🧹 URL limpa, loginParam removido');
            console.log('✅ AUTENTICAÇÃO COMPLETA - NÃO VAI REDIRECIONAR');
            console.log('====================================');
            return; // IMPORTANTE: Retorna para não executar mais nada
          } else {
            console.log('❌ AuthSystem.login retornou false - redirecionando para login');
            Navigation.goToLogin();
            return;
          }
        } catch (error) {
          console.log('❌ Erro ao processar dados da URL:', error);
          console.error('❌ Tipo do erro:', error.name);
          console.error('❌ Mensagem:', error.message);
          console.error('❌ Stack:', error.stack);
          
          // Redirecionar para login se houver erro ao processar
          console.log('🔄 Erro ao processar login - redirecionando para login');
          Navigation.goToLogin();
          return;
        }
      }

      // 2) Verificação final de autenticação SOMENTE se NÃO veio via loginParam
      // Se acabou de fazer login via URL, não precisa verificar - já está autenticado
      if (!loginParam) {
        console.log('🔐 VERIFICANDO AUTENTICAÇÃO (acesso direto sem loginParam)...');
        const estaAutenticado = AuthSystem.isAuthenticated();
        console.log('🔐 isAuthenticated retornou:', estaAutenticado);
        
        if (estaAutenticado) {
          const current = AuthSystem.getCurrentUser();
          console.log('👤 Dados do usuário carregados:', current);
          console.log('📛 Nome do usuário:', current?.nome);
          setUser(current);
        } else {
          console.log('🔒 Não autenticado - redirecionando para login');
          Navigation.goToLogin();
        }
      } else {
        console.log('✅ Login via URL - autenticação já validada, não redirecionar');
      }
    };

    bootstrapAuth();

    // Carregar tarefas inicialmente
    carregarTarefas();

    // Fechar sidebar quando redimensionar janela
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarActive(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Badge de pendentes para aprovação em Compras
  useEffect(() => {
    if (!user?.nome) return;
    const apiBase = window.location.hostname !== 'localhost'
      ? 'https://erp-backend-monolith.onrender.com/api/compras-solicitacoes'
      : 'http://localhost:5000/api/compras-solicitacoes';
    fetch(`${apiBase}/solicitacoes/pendentes-aprovacao?nome=${encodeURIComponent(user.nome)}`)
      .then(r => r.json())
      .then(res => { if (res.success) setBadgeCompras(res.data.count); })
      .catch(() => {});
  }, [user]);

  // Função para toggle do menu hamburger
  const toggleSidebar = () => {
    setSidebarActive(!sidebarActive);
  };

  // Função para fechar sidebar
  const closeSidebar = () => {
    setSidebarActive(false);
  };

  // Função para navegar para licitação
  const goToLicitacao = (e) => {
    e.preventDefault();
    if (!PermissionHelper.temPermissao('DASHBOARD_LICITACAO') && !PermissionHelper.isAdmFull()) {
      alert('⚠️ Você não tem permissão para acessar o módulo Licitações.');
      return;
    }
    window.location.href = '/licitacao';
  };

  // Função para navegar para Relatórios Power BI
  const goToRelatoriosPowerBI = () => {
    window.location.href = '/relatorios-power-bi';
  };

  // Função para navegar para SEI Interno
  const goToSeiInterno = () => {
    window.location.href = '/sei-interno';
  };

  // Função para navegar para Solicitar Compra
  const goToSolicitarCompra = () => {
    window.location.href = '/solicitar-compra';
  };

  // Função para navegar para Base de Contratos Ativos
  const goToBaseContratosAtivos = () => {
    window.location.href = '/base-contratos-ativos';
  };

  // Função para navegar para Compras
  const goToCompras = () => {
    if (!PermissionHelper.temPermissao('DASHBOARD_COMPRAS') && !PermissionHelper.isAdmFull()) {
      alert('⚠️ Você não tem permissão para acessar o módulo Compras.');
      return;
    }
    window.location.href = '/compras';
  };

  // Função para navegar para Financeiro
  const goToFinanceiro = () => {
    if (!PermissionHelper.temPermissao('DASHBOARD_FINANCEIRO') && !PermissionHelper.isAdmFull()) {
      alert('⚠️ Você não tem permissão para acessar o módulo Financeiro.');
      return;
    }
    window.location.href = '/financeiro';
  };

  // Função para navegar para Operacional
  const goToOperacional = () => {
    if (!PermissionHelper.temPermissao('DASHBOARD_OPERACIONAL') && !PermissionHelper.isAdmFull()) {
      alert('⚠️ Você não tem permissão para acessar o módulo Operacional.');
      return;
    }
    window.location.href = '/operacional';
  };

  // Função para navegar para Treinamentos
  const goToTreinamentos = () => {
    if (!PermissionHelper.temPermissao('DASHBOARD_TREINAMENTOS') && !PermissionHelper.isAdmFull()) {
      alert('⚠️ Você não tem permissão para acessar o módulo Treinamentos.');
      return;
    }
    window.location.href = '/treinamentos';
  };

  // Função para navegar para Recursos Humanos
  const goToRecursosHumanos = () => {
    const usuario = PermissionHelper.getUsuario();
    const papel = usuario?.permissoes?.papel;
    if (papel !== 'rh-full' && papel !== 'adm-full' && !PermissionHelper.isAdmFull()) {
      alert('⚠️ Você não tem permissão para acessar o módulo Recursos Humanos.');
      return;
    }
    window.location.href = '/recursos-humanos';
  };

  // Função para logout
  const handleLogout = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevenir propagação do evento
    console.log('🚪 Fazendo logout...');
    
    // Limpar TODA a sessão de forma forçada
    sessionStorage.clear();
    localStorage.clear();
    console.log('✅ Sessão limpa completamente');
    
    // Redirecionar para login IMEDIATAMENTE sem dados
    window.location.replace(FRONTEND_URLS.LOGIN);
  };

  // Função para toggle dropdown
  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  // Função para fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownOpen && !event.target.closest('.user-dropdown')) {
        setUserDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userDropdownOpen]);

  // Função para upload de imagem
  const handleImageUpload = () => {
    console.log('🖱️ Botão anexar clicado');
    const input = document.getElementById('imageUploadInput');
    console.log('📂 Input encontrado:', input);
    if (input) {
      input.click();
      console.log('✅ Input clicado');
    } else {
      console.error('❌ Input não encontrado');
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    console.log('🎯 handleFileChange chamado');
    const file = e.target.files[0];
    console.log('📁 Arquivo capturado:', file);
    
    if (file) {
      console.log('📸 Arquivo selecionado:', file.name, file.type);
      
      // Verificar se é uma imagem
      if (file.type.startsWith('image/')) {
        try {
          // Criar URL para preview da imagem
          const imageUrl = URL.createObjectURL(file);
          console.log('🔗 URL criada:', imageUrl);
          
          const imageData = {
            url: imageUrl,
            name: file.name,
            size: (file.size / 1024 / 1024).toFixed(2) + ' MB'
          };
          
          console.log('💾 Dados da imagem:', imageData);
          setUploadedImage(imageData);
          
          console.log('✅ Estado atualizado com sucesso!');
          
          // Limpar o valor do input
          e.target.value = '';
        } catch (error) {
          console.error('❌ Erro ao processar imagem:', error);
          alert('Erro ao carregar a imagem. Tente novamente.');
        }
      } else {
        console.log('❌ Tipo de arquivo inválido:', file.type);
        alert('❌ Por favor, selecione apenas arquivos de imagem (PNG, JPG, GIF, etc.)');
        e.target.value = '';
      }
    } else {
      console.log('❌ Nenhum arquivo selecionado');
    }
  };

  // Funções de gerenciamento de tarefas
  const handleNovaTarefa = () => {
    setTarefaEdicao(null);
    setTarefasModalOpen(true);
  };

  const handleEditarTarefa = (tarefa) => {
    setTarefaEdicao(tarefa);
    setTarefasModalOpen(true);
  };

  const handleSalvarTarefa = async (formData) => {
    try {
      const currentUser = AuthSystem.getCurrentUser();
      
      if (tarefaEdicao) {
        // Atualizar tarefa existente
        const response = await fetch(`${API_URLS.TAREFAS}/${tarefaEdicao.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        const data = await response.json();
        
        if (data.success) {
          alert('✅ Tarefa atualizada com sucesso!');
          carregarTarefas();
          setTarefasModalOpen(false);
          setTarefaEdicao(null);
        } else {
          alert('❌ Erro ao atualizar tarefa: ' + data.message);
        }
      } else {
        // Criar nova tarefa
        const response = await fetch(API_URLS.TAREFAS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            criado_por: currentUser?.cpf
          })
        });

        const data = await response.json();
        
        if (data.success) {
          alert('✅ Tarefa criada com sucesso!');
          carregarTarefas();
          setTarefasModalOpen(false);
        } else {
          alert('❌ Erro ao criar tarefa: ' + data.message);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao salvar tarefa:', error);
      alert('❌ Erro ao salvar tarefa. Verifique a conexão com o servidor.');
    }
  };

  const handleDeletarTarefa = async (tarefaId) => {
    if (!window.confirm('❓ Tem certeza que deseja deletar esta tarefa?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URLS.TAREFAS}/${tarefaId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        alert('✅ Tarefa deletada com sucesso!');
        carregarTarefas();
      } else {
        alert('❌ Erro ao deletar tarefa: ' + data.message);
      }
    } catch (error) {
      console.error('❌ Erro ao deletar tarefa:', error);
      alert('❌ Erro ao deletar tarefa. Verifique a conexão com o servidor.');
    }
  };

  return (
    <div className="dashboard-container">
      <Particles />
      <aside className={`sidebar ${sidebarActive ? 'active' : ''}`}>
        <div className="sidebar-header">
          <Logo />
        </div>
        
        <div className="user-info">
          <div>
            <span className="username">{user?.username || 'Usuário'}</span>
            <span className="role">{user?.role || 'Função'}</span>
          </div>
          <i className="fas fa-caret-down"></i>
        </div>

        <nav className="sidebar-nav">
          <ul>
            <li className="nav-item">
              <a href="#"><i className="fas fa-file-archive"></i> Arquivo Digital</a>
            </li>
            <li className="nav-item">
              <a href="#"><i className="fas fa-chart-line"></i> Controladoria</a>
            </li>
            <li className="nav-item">
              <a href="#" onClick={(e) => { e.preventDefault(); goToCompras(); }}>
                <i className="fas fa-shopping-cart"></i> Compras
              </a>
            </li>
            <li className="nav-item">
              <a href="#" onClick={(e) => { e.preventDefault(); goToFinanceiro(); }}>
                <i className="fas fa-dollar-sign"></i> Financeiro
              </a>
            </li>
            <li className="nav-item">
              <a href="#"><i className="fas fa-gavel"></i> Jurídico</a>
            </li>
            <li className="nav-item">
              <a href="#" onClick={goToLicitacao}>
                <i className="fas fa-handshake"></i> Licitações
              </a>
            </li>
            <li className="nav-item">
              <a href="#" onClick={(e) => { e.preventDefault(); goToOperacional(); }}>
                <i className="fas fa-cogs"></i> Operacional
              </a>
            </li>
            <li className="nav-item">
              <a href="#" onClick={(e) => { e.preventDefault(); goToTreinamentos(); }}>
                  <i className="fas fa-graduation-cap"></i> Recrutamento e Seleção
              </a>
            </li>
            <li className="nav-item">
              <a href="#"><i className="fas fa-check-circle"></i> Qualidade</a>
            </li>
            <li className="nav-item">
              <a href="#" onClick={(e) => { e.preventDefault(); goToRecursosHumanos(); }}>
                <i className="fas fa-users"></i> Recursos Humanos
              </a>
            </li>
            <li className="nav-item">
              <a href="#"><i className="fas fa-hard-hat"></i> Segurança do Trabalho</a>
            </li>
            {PermissionHelper.isAdmFull() && (
              <li className="nav-item">
                <a href="#" onClick={(e) => { e.preventDefault(); setPermissoesModalOpen(true); }}>
                  <i className="fas fa-user-shield"></i> Permissões
                </a>
              </li>
            )}
            <li className="nav-item">
              <a href="#" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i> Voltar para Login
              </a>
            </li>
          </ul>
        </nav>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <button 
            id="hamburgerMenu" 
            className={`hamburger-menu ${sidebarActive ? 'active' : ''}`}
            onClick={toggleSidebar}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          <div className="search-box">
            <input type="text" placeholder="Search..." />
            <i className="fas fa-search"></i>
          </div>
          <div className="topbar-icons">
            <i className="fas fa-envelope"></i>
            <i className="fas fa-bell"></i>
            <div className="user-dropdown" style={{position: 'relative'}}>
              <div 
                onClick={toggleUserDropdown}
                style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'}}
              >
                <span>{user?.nome || user?.username || 'Usuário'}</span>
                <i className="fas fa-caret-down"></i>
              </div>
              {userDropdownOpen && (
                <div 
                  className="dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '150px',
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}
                >
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '14px',
                      color: '#333',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <i className="fas fa-sign-out-alt" style={{color: '#e74c3c'}}></i>
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="dashboard-body">
          <h1>Dashboard</h1>
          
          {/* BOTÃO TEMPORÁRIO PARA SIMULAR LOGIN */}
          {!user && (
            <div style={{margin: '20px 0', padding: '20px', backgroundColor: '#f0f0f0', borderRadius: '8px'}}>
              <h3>🔧 SIMULAR LOGIN</h3>
              <p>Você não está logado. Use o botão abaixo para simular um login:</p>
              <button 
                onClick={() => {
                  console.log('🔄 Iniciando processo de login...');
                  
                  // Limpar sessão anterior
                  sessionStorage.clear();
                  console.log('🧹 SessionStorage limpo');
                  
                  // Salvar novo login no sessionStorage
                  const userData = {
                    userId: 1,
                    username: 'Eduardo',
                    role: 'Administrador',
                    permissions: ['all'],
                    loginTime: new Date().toISOString()
                  };
                  
                  sessionStorage.setItem('erp_user_session', JSON.stringify(userData));
                  sessionStorage.setItem('erp_login_time', userData.loginTime);
                  console.log('💾 Dados salvos:', userData);
                  
                  // Verificar autenticação novamente
                  console.log('🔄 Verificando autenticação após login...');
                  checkAuthentication();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginRight: '10px'
                }}
              >
                🔑 Fazer Login como Eduardo
              </button>
              
              <button 
                onClick={() => {
                  sessionStorage.clear();
                  setUser(null);
                  console.log('🚪 Logout realizado');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🚪 Fazer Logout
              </button>
            </div>
          )}

          <div className="card-stats-row">
            <div 
              className="card stat-card orange"
              onClick={goToBaseContratosAtivos}
              style={{ cursor: 'pointer' }}
            >
              <span className="title"><h2>Base de Contratos Ativos</h2></span>
              <span className="value"><h6> </h6></span>
            </div>
            <div className="card stat-card green">
              <span className="title"><h2>Malote</h2></span>
              <span className="value"><h6> </h6></span>
            </div>
            <div 
              className="card stat-card red" 
              onClick={goToRelatoriosPowerBI}
              style={{ cursor: 'pointer' }}
            >
              <span className="title"><h2>Relatórios Power BI</h2></span>
            </div>
            <div 
              className="card stat-card blue"
              onClick={goToSeiInterno}
              style={{ cursor: 'pointer' }}
            >
              <span className="title"><h2>SEI interno - Nascimento</h2></span>
            </div>
          </div>

          <div className="card-stats-row">
            <div className="card stat-card orange" onClick={goToSolicitarCompra} style={{ cursor: 'pointer', position: 'relative' }}>
              <span className="title"><h2>Solicitar Compras</h2></span>
              {badgeCompras > 0 && (
                <span style={{
                  position: 'absolute', top: 10, right: 14,
                  background: '#ff4444', color: '#fff', fontWeight: 800,
                  fontSize: 13, minWidth: 24, height: 24,
                  borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 6px', boxShadow: '0 2px 6px rgba(0,0,0,.3)'
                }}>{badgeCompras}</span>
              )}
            </div>
            <div className="card stat-card green" onClick={() => setShowModalSistemas(true)} style={{ cursor: 'pointer' }}>
              <span className="title"><h2>Solicitar para Sistemas</h2></span>
            </div>
            <div className="card stat-card red" style={{ cursor: 'default', opacity: 0.5 }}>
              <span className="title"><h2>Em breve</h2></span>
            </div>
            <div className="card stat-card blue" style={{ cursor: 'default', opacity: 0.5 }}>
              <span className="title"><h2>Em breve</h2></span>
            </div>
          </div>

          <div className="main-grid">
            <div className="card task-card card-small">
              <div className="world-map-header" style={{ marginBottom: '16px' }}>
                <div>
                  <div className="title-box">
                    <h2>Suas Tarefas</h2>
                  </div>
                </div>
                {PermissionHelper.isAdmFull() && (
                  <button 
                    className="attach-image-btn"
                    onClick={handleNovaTarefa}
                    title="Criar Nova Tarefa"
                    type="button"
                    style={{ marginRight: '18px' }}
                  >
                    <i className="fas fa-plus"></i>
                    Nova Tarefa
                  </button>
                )}
              </div>
              
              <div className="task-list">
                {tarefas.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                    <i className="fas fa-tasks" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}></i>
                    <p>Nenhuma tarefa encontrada</p>
                  </div>
                ) : (
                  tarefas.map((tarefa) => {
                    const statusClass = 
                      tarefa.status === 'Pendente' ? 'status-pending' :
                      tarefa.status === 'Em Progresso' ? 'status-progress' : 
                      'status-completed';
                    
                    const prazoFormatado = tarefa.prazo 
                      ? new Date(tarefa.prazo).toLocaleDateString('pt-BR') 
                      : 'Sem prazo';

                    return (
                      <div key={tarefa.id} className={`task-item ${statusClass}`}>
                        <div className="task-content">
                          <span className="task-title">{tarefa.titulo}</span>
                          <span className="task-date">
                            {tarefa.status === 'Finalizado' ? 'Concluído' : 'Prazo'}: {prazoFormatado}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div className="task-status">{tarefa.status}</div>
                          {PermissionHelper.isAdmFull() && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleEditarTarefa(tarefa)}
                                title="Editar Tarefa"
                                style={{
                                  padding: '6px 12px',
                                  background: 'linear-gradient(135deg, #ff6b35, #cc5529)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  transition: 'all 0.3s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  fontWeight: '500'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #ff8c5a, #ff6b35)';
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(255, 107, 53, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #ff6b35, #cc5529)';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={() => handleDeletarTarefa(tarefa.id)}
                                title="Deletar Tarefa"
                                style={{
                                  padding: '6px 12px',
                                  background: 'linear-gradient(135deg, #f34c67, #c0392b)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  transition: 'all 0.3s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  fontWeight: '500'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #ff6b7a, #f34c67)';
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(243, 76, 103, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #f34c67, #c0392b)';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="card world-map-card">
              <div className="world-map-header">
                <div>
                  <div className="title-box">
                    <h2>Canal Informa Nascimento</h2>
                  </div>
                  <p className="subtitle">Informações Gerais da Nascimento</p>
                </div>
                {PermissionHelper.isAdmFull() && (
                  <>
                    <button 
                      className="attach-image-btn"
                      onClick={handleImageUpload}
                      type="button"
                    >
                      <i className="fas fa-paperclip"></i>
                      Anexar Imagem
                    </button>
                    <input 
                      type="file" 
                      id="imageUploadInput" 
                      className="image-upload-input" 
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </>
                )}
              </div>
              
              <div className="world-map-simulation">
                {uploadedImage ? (
                  <div className="uploaded-image-container">
                    <div className="image-preview">
                      <img 
                        className="map-image"
                        src={uploadedImage.url} 
                        alt="Canal Informa" 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }}
                        onLoad={() => console.log('🖼️ Imagem carregada na tela')}
                        onError={() => console.error('❌ Erro ao carregar imagem')}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="uploaded-image-container">
                    <div className="image-preview">
                      <img 
                        className="map-image"
                        src="/canal-informa-nova.png" 
                        alt="Canal Informa Nascimento" 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }}
                      />
                    </div>
                  </div>
                ) }
                {false && (
                  <div style={{minHeight: '100px'}}>
                    {/* Área vazia - só aparece imagem quando selecionada */}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Overlay para mobile */}
      <div 
        className={`sidebar-overlay ${sidebarActive ? 'active' : ''}`}
        onClick={closeSidebar}
      ></div>

      {/* Modal de Permissões */}
      <ModalPermissoes 
        isOpen={permissoesModalOpen} 
        onClose={() => setPermissoesModalOpen(false)} 
      />

      {/* Modal de Tarefas */}
      <ModalTarefas
        isOpen={tarefasModalOpen}
        onClose={() => {
          setTarefasModalOpen(false);
          setTarefaEdicao(null);
        }}
        tarefa={tarefaEdicao}
        onSave={handleSalvarTarefa}
      />

      {showModalSistemas && (
        <div
          onClick={() => setShowModalSistemas(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#2d2d44', borderRadius: 14, padding: '32px 28px',
              width: '100%', maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,.6)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700 }}>Solicitar para Sistemas</h3>
              <button
                onClick={() => setShowModalSistemas(false)}
                style={{ background: 'none', border: 'none', color: '#aaa', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <button
                onClick={() => { setShowModalSistemas(false); window.location.href = '/solicitar-erp'; }}
                style={{
                  background: '#e67e22', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '14px 20px', cursor: 'pointer', fontWeight: 700, fontSize: 15,
                  textAlign: 'left', lineHeight: 1.3
                }}
              >
                Solicitar para o Sistema da ERP
              </button>
              <button
                onClick={() => { setShowModalSistemas(false); window.location.href = '/solicitar-setor-sistemas'; }}
                style={{
                  background: '#4a90e2', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '14px 20px', cursor: 'pointer', fontWeight: 700, fontSize: 15,
                  textAlign: 'left', lineHeight: 1.3
                }}
              >
                 Solicitar ao Setor de Sistemas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrapper com Router
function AppWithRouter() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/base-contratos-ativos" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <BaseContratosAtivos />
          </React.Suspense>
        } />
        <Route path="/relatorios-power-bi" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <RelatoriosPowerBi />
          </React.Suspense>
        } />
        <Route path="/compras/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <ComprasModule />
          </React.Suspense>
        } />
        <Route path="/licitacao/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <LicitacaoModule />
          </React.Suspense>
        } />
        <Route path="/financeiro/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <FinanceiroModule />
          </React.Suspense>
        } />
        <Route path="/operacional/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <OperacionalModule />
          </React.Suspense>
        } />
        <Route path="/treinamentos/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <TreinamentosModule />
          </React.Suspense>
        } />
        <Route path="/recursos-humanos/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <RecursosHumanosModule />
          </React.Suspense>
        } />
        <Route path="/sei-interno/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <SeiInternoModule />
          </React.Suspense>
        } />
        <Route path="/solicitar-compra/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <SolicitarCompraApp />
          </React.Suspense>
        } />
        <Route path="/solicitar-erp/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <SolicitarErpApp />
          </React.Suspense>
        } />
        <Route path="/solicitar-setor-sistemas/*" element={
          <React.Suspense fallback={<div>Carregando...</div>}>
            <SolicitarSetorSistemasApp />
          </React.Suspense>
        } />
      </Routes>
    </Router>
  );
}

export default AppWithRouter;